import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Bank, Factory, Storefront, Heartbeat, Lightbulb, Check, Buildings } from "@phosphor-icons/react";
import { useMarketing } from "@/components/marketing/MarketingLayout";
import {
  NAVY, GOLD, GREEN, Reveal, Overline, EditorialHero,
} from "@/components/marketing/editorial";

export const INDUSTRY_DATA = {
  banking: {
    icon: Bank, label: "Banking & Financial Services",
    title: "Finance AI built for the pace", italic: "banking demands.",
    sub: "Regulatory reporting, GL and nostro reconciliations, and audit trails that have to be right the first time — every time.",
    problem: "Banking finance teams run on tight regulatory deadlines and near-zero tolerance for error. A single unreconciled GL line or a missed compliance check doesn't just slow down close — it becomes an examiner finding. Most teams still catch these issues the hard way: manually, after the fact, during month-end or an audit.",
    capabilities: [
      "Reconciles GL, nostro and vostro positions overnight, flagging breaks before they reach month-end",
      "Validates every transaction against current compliance and tax rules as it's booked, not after",
      "Keeps a line-by-line evidence trail ready for internal and external auditors on demand",
      "Surfaces regulatory reporting packages with source-document traceability built in",
    ],
    stats: [["2x", "Faster regulatory close"], ["90%+", "Recs auto-matched"], ["0", "Untraceable entries"]],
    cta: "See how banking teams close faster",
    story: {
      profile: "Regional bank · multi-branch footprint",
      narrative: "Every month-end began the same way: a scramble to untangle nostro breaks and chase down unreconciled GL lines before anyone could trust the numbers, let alone show them to an examiner.",
      outcome: "After plugging Vaku's agents into daily reconciliation, breaks got caught and cleared overnight instead of piling up. The close became predictable, and the team walked into its next regulatory exam with a clean, traceable evidence trail instead of a last-minute scramble.",
    },
  },
  manufacturing: {
    icon: Factory, label: "Manufacturing & Distribution",
    title: "Keep multi-entity finance in sync —", italic: "automatically.",
    sub: "Complex BOMs, intercompany flows and inventory-driven AP/AR don't have to mean a slower close.",
    problem: "Manufacturers run finance across multiple entities, plants and currencies, with intercompany transactions and inventory-linked costs threading through every ledger. Reconciling all of it by month-end usually means days of manual netting and matching before anyone can trust the numbers.",
    capabilities: [
      "Matches goods-receipt to invoice automatically, even across partial shipments and price variances",
      "Nets intercompany balances across entities before they become a close bottleneck",
      "Rolls up multi-entity, multi-currency financials on a daily cadence, not just at month-end",
      "Flags material cost drift against standard cost as it happens",
    ],
    stats: [["3 days", "Typical close, down from 7+"], ["80%+", "Invoices auto-matched"], ["Daily", "Multi-entity rollups"]],
    cta: "See how manufacturers close in days, not weeks",
    story: {
      profile: "Multi-plant manufacturer · operations across several countries",
      narrative: "Intercompany balances were netted by hand every month-end, and goods-receipt mismatches were usually only discovered after the books had already closed — turning close week into overtime week.",
      outcome: "With matching and intercompany netting running continuously in the background, the finance team shifted from firefighting at month-end to reviewing exceptions as they happened, and close stopped eating into everyone's weekend.",
    },
  },
  retail: {
    icon: Storefront, label: "Retail & Consumer",
    title: "Cash application and forecasting", italic: "at retail's pace.",
    sub: "High-volume, low-value transactions and sharp seasonal swings shouldn't mean a forecast that's always a step behind.",
    problem: "Retail and consumer finance teams deal with thousands of small transactions, remittance-light payments, and seasonal demand that can swing the forecast overnight. By the time a monthly model is rebuilt, the business has already moved on.",
    capabilities: [
      "Auto-matches high-volume, remittance-light payments against open invoices",
      "Re-prioritizes collections automatically as payment patterns shift by season",
      "Refreshes the rolling forecast nightly against POS and payment data, not monthly",
      "Flags vendor rebate and chargeback discrepancies before they erode margin",
    ],
    stats: [["Nightly", "Forecast refresh cycle"], ["70%+", "Cash auto-applied"], ["Fewer", "Unexplained margin leaks"]],
    cta: "See a retail-pace forecast in action",
    story: {
      profile: "Consumer retailer · high transaction volume, strong seasonality",
      narrative: "Collections were perpetually behind on thousands of small, remittance-light payments, and by the time the monthly forecast was published, the business had already moved on from the assumptions behind it.",
      outcome: "Once payments started auto-matching and the forecast refreshed against daily POS data, collections worked a short, current list instead of chasing old balances, and leadership began planning against numbers that reflected what was actually happening in stores that week.",
    },
  },
  healthcare: {
    icon: Heartbeat, label: "Healthcare & Life Sciences",
    title: "Continuous controls, not a", italic: "quarterly scramble.",
    sub: "Payer reconciliations, fund restrictions and audit readiness, handled every day — not just before the audit.",
    problem: "Healthcare and life sciences finance teams juggle payer remittances, restricted grant funds, and audit requirements that never really go away. Most of this still gets reconciled in a once-a-quarter scramble, which means problems are found months after they started.",
    capabilities: [
      "Reconciles payer remittances line-by-line against claims, not in aggregate",
      "Tracks fund and grant restrictions automatically so spend never drifts off-purpose",
      "Maintains a standing audit-readiness scorecard instead of a pre-audit fire drill",
      "Flags coding or billing mismatches before they become a compliance issue",
    ],
    stats: [["Daily", "Payer rec cadence"], ["100%", "Fund restriction tracking"], ["Weeks", "Saved on audit prep"]],
    cta: "See continuous controls built for healthcare",
    story: {
      profile: "Multi-facility healthcare provider",
      narrative: "Payer remittances and restricted grant funds were reconciled in a once-a-quarter push, which meant problems were usually discovered months after they'd actually started.",
      outcome: "Moving that reconciliation to a daily, line-by-line cadence meant issues surfaced and got resolved almost as soon as they appeared, and the team walked into its next audit with a standing readiness scorecard instead of a scramble to reconstruct what happened.",
    },
  },
  tech: {
    icon: Lightbulb, label: "Technology & Professional Services",
    title: "Multi-currency consolidation,", italic: "resolved same-day.",
    sub: "Recurring revenue and services-based billing create AR and reporting complexity most finance stacks aren't built for.",
    problem: "Technology and professional services finance teams deal with recurring revenue, usage-based billing and multi-currency entities — all of which complicate revenue recognition and make month-end consolidation a multi-day exercise.",
    capabilities: [
      "Validates revenue recognition against contract terms automatically, not spreadsheet by spreadsheet",
      "Consolidates multi-currency entities same-day instead of over a multi-day close",
      "Answers ad-hoc finance questions — MRR, churn impact, runway — in natural language",
      "Flags unbilled or mis-billed usage before it reaches the invoice",
    ],
    stats: [["Same-day", "Multi-currency consolidation"], ["Faster", "Rev-rec validation"], ["Instant", "Natural-language Q&A"]],
    cta: "See same-day consolidation for tech & services",
    story: {
      profile: "Fast-growing software company · multi-currency customer base",
      narrative: "Closing the books took several days each month just to get a trustworthy consolidated view, while revenue recognition was tracked largely by hand against contract terms.",
      outcome: "With revenue recognition validated automatically and entities consolidating same-day, the team compressed its close dramatically and could finally answer board questions about MRR and runway in the moment they were asked, instead of promising an answer next week.",
    },
  },
};

export default function IndustryPage() {
  const { industryId } = useParams();
  const { openDemo } = useMarketing();
  const data = INDUSTRY_DATA[industryId];
  if (!data) return <Navigate to="/solutions" replace />;
  const Icon = data.icon;
  const others = Object.entries(INDUSTRY_DATA).filter(([id]) => id !== industryId);

  return (
    <>
      <EditorialHero eyebrow={`Solutions · ${data.label}`} title={data.title} italic={data.italic} sub={data.sub} />

      <section className="py-20 sm:py-28 border-t bg-white" style={{ borderColor: `${NAVY}10` }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-10">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${GOLD}1A` }}>
              <Icon size={26} weight="duotone" style={{ color: GOLD }} />
            </div>
            <Overline>The challenge</Overline>
          </div>
          <Reveal>
            <p className="text-lg sm:text-xl font-light leading-relaxed" style={{ color: `${NAVY}CC` }}>{data.problem}</p>
          </Reveal>

          <div className="mt-16">
            <Overline>How Vaku helps</Overline>
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              {data.capabilities.map((c, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="flex items-start gap-3 rounded-xl p-5" style={{ border: `1px solid ${NAVY}15` }}>
                    <Check size={18} weight="bold" className="mt-0.5 shrink-0" style={{ color: GREEN }} />
                    <p className="text-sm font-light leading-relaxed" style={{ color: `${NAVY}CC` }}>{c}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="mt-16 grid grid-cols-3 gap-4">
            {data.stats.map(([n, l]) => (
              <div key={l} className="rounded-xl p-6 bg-white text-center" style={{ border: `1px solid ${NAVY}1F` }}>
                <div className="font-serif font-light text-3xl sm:text-4xl" style={{ color: GOLD }}>{n}</div>
                <div className="text-xs mt-2 font-light" style={{ color: `${NAVY}B3` }}>{l}</div>
              </div>
            ))}
          </div>

          <div className="mt-16">
            <Overline>Customer story</Overline>
            <Reveal className="mt-6">
              <div data-testid="industry-customer-story" className="rounded-2xl p-8 sm:p-10"
                   style={{ background: `${GOLD}0D`, border: `1px solid ${NAVY}15` }}>
                <div className="inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3.5 py-1.5 mb-6"
                     style={{ background: `${NAVY}0D`, color: NAVY }}>
                  <Buildings size={14} weight="bold" style={{ color: GOLD }} />
                  {data.story.profile}
                </div>
                <p className="text-base sm:text-lg font-light leading-relaxed" style={{ color: `${NAVY}CC` }}>
                  {data.story.narrative}
                </p>
                <p className="mt-4 text-base sm:text-lg font-light leading-relaxed">
                  <span className="font-semibold" style={{ color: GREEN }}>The outcome — </span>
                  <span style={{ color: `${NAVY}CC` }}>{data.story.outcome}</span>
                </p>
                <p className="mt-6 text-xs font-light italic" style={{ color: `${NAVY}80` }}>
                  Illustrative example based on common patterns Vaku solves for in this industry.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 border-t" style={{ borderColor: `${NAVY}10`, background: NAVY }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-light tracking-tight text-white">
            {data.cta.split(" ").slice(0, -2).join(" ")}{" "}
            <span className="italic" style={{ color: GOLD }}>{data.cta.split(" ").slice(-2).join(" ")}</span>
          </h2>
          <button onClick={() => openDemo(`solutions-${industryId}`)} data-testid="industry-book-demo-button"
            className="mt-8 rounded-full px-7 py-3.5 text-sm font-semibold text-white transition-colors shadow-lg"
            style={{ background: GREEN }}
            onMouseEnter={(e) => (e.currentTarget.style.background = GOLD)}
            onMouseLeave={(e) => (e.currentTarget.style.background = GREEN)}>
            Book a Demo for {data.label}
          </button>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <Overline>Other industries</Overline>
          <div className="mt-5 flex flex-wrap gap-3">
            {others.map(([id, o]) => (
              <Link key={id} to={`/solutions/${id}`} data-testid={`industry-crosslink-${id}`}
                className="inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-4 py-2 transition-colors hover:text-[#C8A24B]"
                style={{ border: `1px solid ${NAVY}1F`, color: `${NAVY}CC` }}>
                {o.label} <ArrowRight size={13} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
